import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePractice } from '../context/PracticeContext';
import api from '../services/api';
import NHLogo from '../components/NHLogo';

const SETUP_ITEMS = [
  {
    key: 'basics',
    label: 'Practice Basics',
    description: 'Set your practice name, address, phone number, and timezone',
    icon: 'building',
    path: '/practice-view/practice-settings',
    time: '~2 min',
  },
  {
    key: 'messaging',
    label: 'Messaging',
    description: 'Configure SMS and email so patients receive messages from your practice',
    icon: 'message',
    path: '/practice-view/practice-settings',
    queryParam: 'tab=messaging',
    time: '~2 min',
  },
  {
    key: 'ehr',
    label: 'EHR Integration',
    description: 'Connect your EHR to sync patients and appointments automatically',
    icon: 'link',
    path: '/practice-view/practice-settings',
    queryParam: 'tab=ehr',
    time: '~3 min',
  },
  {
    key: 'templates',
    label: 'Campaign Templates',
    description: 'Import default campaign types and message templates for patient outreach',
    icon: 'template',
    path: '/practice-view/campaigns',
    queryParam: 'setup=true',
    time: '~1 min',
  },
  {
    key: 'notifications',
    label: 'Appointment Reminders',
    description: 'Set up automated reminder rules to reduce no-shows',
    icon: 'bell',
    path: '/practice-view/notification-center',
    queryParam: 'setup=true',
    time: '~1 min',
  },
  {
    key: 'voice',
    label: 'Voice AI',
    description: 'Enable AI-powered phone calls for outreach and inbound routing',
    icon: 'phone',
    path: '/practice-view/voice-ai',
    time: '~3 min',
  },
  {
    key: 'reputation',
    label: 'Reputation',
    description: 'Collect patient feedback and drive Google reviews automatically',
    icon: 'star',
    path: '/practice-view/reputation',
    time: '~2 min',
  },
  {
    key: 'intake',
    label: 'Intake Forms',
    description: 'Import a predefined intake template for patient questionnaires',
    icon: 'clipboard',
    path: '/practice-view/care-hub?tab=intake',
    queryParam: 'setup=true',
    time: '~1 min',
  },
];

const ICONS = {
  building: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22V12h6v10" /><path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01" />
    </svg>
  ),
  message: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  link: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  ),
  template: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
    </svg>
  ),
  bell: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  ),
  phone: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  star: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  clipboard: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" />
    </svg>
  ),
};

function Onboarding() {
  const navigate = useNavigate();
  const { selectedPracticeId, selectedPractice, loadOnboardingStatus } = usePractice();
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!selectedPracticeId) return;
      try {
        const statusRes = await api.getOnboardingStatus(selectedPracticeId);
        if (statusRes.success && statusRes.data) {
          setProgress(statusRes.data.progress || {});
        }
      } catch (err) {
        console.error('Failed to load onboarding data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedPracticeId]);

  const saveProgress = useCallback(async (updatedProgress) => {
    try {
      await api.updateOnboardingProgress(selectedPracticeId, updatedProgress);
    } catch (err) {
      console.error('Failed to save onboarding progress:', err);
    }
  }, [selectedPracticeId]);

  const toggleDone = useCallback((key) => {
    const updated = { ...progress, [key]: !progress[key] };
    setProgress(updated);
    saveProgress(updated);
  }, [progress, saveProgress]);

  const handleFinish = useCallback(async () => {
    try {
      await api.completeOnboarding(selectedPracticeId);
      await loadOnboardingStatus(selectedPracticeId);
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
    }
    navigate('/practice-view');
  }, [selectedPracticeId, navigate, loadOnboardingStatus]);

  const handleDismiss = useCallback(() => {
    navigate('/practice-view');
  }, [navigate]);

  const handleGoTo = (item) => {
    const url = item.queryParam ? `${item.path}${item.path.includes('?') ? '&' : '?'}${item.queryParam}` : item.path;
    navigate(url);
  };

  const completedCount = SETUP_ITEMS.filter(i => progress[i.key]).length;
  const progressPercent = Math.round((completedCount / SETUP_ITEMS.length) * 100);

  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.spinner} />
        <p style={{ color: '#6b7280', marginTop: 16 }}>Loading setup...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <NHLogo size={26} light />
          <span style={styles.headerDivider} />
          <span style={styles.headerTitle}>Practice Setup</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={styles.skipBtn} onClick={handleDismiss}>Skip for Now</button>
          {completedCount > 0 && (
            <button style={styles.finishBtn} onClick={handleFinish}>
              Finish Setup
            </button>
          )}
        </div>
      </div>

      {/* Welcome banner */}
      <div style={styles.banner}>
        <div>
          <h1 style={styles.bannerTitle}>
            Welcome{selectedPractice?.name ? `, ${selectedPractice.name}` : ''}!
          </h1>
          <p style={styles.bannerSubtitle}>
            Complete the items below to get your practice fully set up. Each card links to the relevant settings page. You can do these in any order and come back anytime.
          </p>
        </div>
        <div style={styles.progressRing}>
          <svg width="80" height="80" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <circle
              cx="40" cy="40" r="34" fill="none"
              stroke="#10b981" strokeWidth="6"
              strokeDasharray={`${2 * Math.PI * 34}`}
              strokeDashoffset={`${2 * Math.PI * 34 * (1 - progressPercent / 100)}`}
              strokeLinecap="round"
              transform="rotate(-90 40 40)"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <span style={styles.progressText}>{completedCount}/{SETUP_ITEMS.length}</span>
        </div>
      </div>

      {/* Checklist cards */}
      <div style={styles.cardGrid}>
        {SETUP_ITEMS.map(item => {
          const done = !!progress[item.key];
          return (
            <div
              key={item.key}
              style={{
                ...styles.card,
                ...(done ? styles.cardDone : {}),
              }}
            >
              <div style={styles.cardTop}>
                <div style={{
                  ...styles.iconCircle,
                  backgroundColor: done ? '#d1fae5' : '#eff6ff',
                  color: done ? '#059669' : '#3b82f6',
                }}>
                  {ICONS[item.icon]}
                </div>
                <button
                  style={{
                    ...styles.checkbox,
                    backgroundColor: done ? '#10b981' : '#fff',
                    borderColor: done ? '#10b981' : '#d1d5db',
                  }}
                  onClick={() => toggleDone(item.key)}
                  title={done ? 'Mark as incomplete' : 'Mark as done'}
                >
                  {done && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 7.5l2.5 2.5 5.5-5.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
              <h3 style={styles.cardLabel}>{item.label}</h3>
              <p style={styles.cardDesc}>{item.description}</p>
              <div style={styles.cardFooter}>
                <span style={styles.cardTime}>{item.time}</span>
                <button style={styles.goBtn} onClick={() => handleGoTo(item)}>
                  {done ? 'Review' : 'Set Up'} →
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingScreen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #e5e7eb',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 32px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { fontSize: 18, fontWeight: 700, color: '#3b82f6' },
  headerDivider: { width: 1, height: 20, backgroundColor: '#d1d5db' },
  headerTitle: { fontSize: 15, fontWeight: 500, color: '#6b7280' },
  skipBtn: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
  finishBtn: {
    padding: '8px 20px',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  banner: {
    margin: '24px 32px 0',
    padding: '28px 32px',
    backgroundColor: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
  },
  bannerTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
  },
  bannerSubtitle: {
    margin: '8px 0 0',
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 1.5,
    maxWidth: 560,
  },
  progressRing: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressText: {
    position: 'absolute',
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
    padding: '24px 32px 48px',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  cardDone: {
    borderColor: '#a7f3d0',
    backgroundColor: '#f0fdf4',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.15s',
  },
  cardLabel: {
    margin: '0 0 6px',
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
  },
  cardDesc: {
    margin: '0 0 16px',
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
    flex: 1,
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTime: {
    fontSize: 12,
    color: '#9ca3af',
    fontWeight: 500,
  },
  goBtn: {
    padding: '6px 14px',
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default Onboarding;
